/**
 * Quantum Terminal — base component library. React 19 + TypeScript, zero UI deps.
 * Styling: components.css + tokens.css. No colour literal appears in this file.
 * No JS hover handlers anywhere: every hover/active/focus state is CSS (audit §4.11).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect,
  useMemo, useRef, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const cx = (...p: Array<string | false | undefined>) => p.filter(Boolean).join(' ');

/**
 * The one reduced-motion check for JS/canvas animation — CSS reads the media
 * query directly (tokens.css / components.css), canvas loops call this. Live:
 * re-reads the media query each call so an OS toggle takes effect without a
 * reload. SSR/no-matchMedia → false (animate).
 */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ───────────────────────────────────────────────────────────── BUTTON ── */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'buy' | 'sell';
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant; size?: 'sm' | 'md'; loading?: boolean; icon?: ReactNode;
}
export const Button = ({ variant = 'secondary', size = 'md', loading, icon, children, className, ...rest }: ButtonProps) => (
  <button
    type="button" data-loading={loading || undefined} aria-busy={loading || undefined}
    disabled={rest.disabled || loading}
    className={cx('qt-btn', `qt-btn--${variant}`, size === 'sm' && 'qt-btn--sm', className)}
    {...rest}
  >
    {icon}{children}
  </button>
);

export const IconButton = ({ label, active, children, ...rest }: { label: string; active?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button type="button" className="qt-iconbtn" aria-label={label} title={label} data-active={active || undefined} {...rest}>{children}</button>
);

/* ────────────────────────────────────────────────────── INPUT FAMILY ── */
export const Input = ({ invalid, mono, className, ...rest }: { invalid?: boolean; mono?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cx('qt-input', mono && 'num', className)} data-invalid={invalid || undefined} aria-invalid={invalid || undefined} {...rest} />
);

/** Drag horizontally on the grip (or the field) to change the value — Tiger/Bookmap
 *  pattern; native spinners are removed globally because they are 12px tall and unhittable. */
export function NumberField({ value, onChange, step = 1, min, max, precision = 2, disabled, id }: {
  value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number;
  precision?: number; disabled?: boolean; id?: string;
}) {
  const drag = useRef<{ x: number; v: number } | null>(null);
  // The raw text while the field is being typed in. Formatting every keystroke
  // with toFixed() moved the caret behind the decimals, so "80" became 8 (live
  // test 24.09.2026). Each parseable draft is applied live; the formatted value
  // comes back on blur.
  const [draft, setDraft] = useState<string | null>(null);
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
  useEffect(() => {
    const move = (e: PointerEvent) => { if (drag.current) onChange(clamp(drag.current.v + Math.round((e.clientX - drag.current.x) / 3) * step)); };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  });
  return (
    <div className="qt-numfield" aria-disabled={disabled || undefined}>
      <input
        id={id} inputMode="decimal" disabled={disabled} value={draft ?? value.toFixed(precision)}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          const n = Number(raw.replace(',', '.'));
          if (raw.trim() !== '' && Number.isFinite(n)) onChange(clamp(n));
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); return; }
          if (e.key === 'ArrowUp') { e.preventDefault(); setDraft(null); onChange(clamp(value + step * (e.shiftKey ? 10 : 1))); }
          if (e.key === 'ArrowDown') { e.preventDefault(); setDraft(null); onChange(clamp(value - step * (e.shiftKey ? 10 : 1))); }
        }}
        role="spinbutton" aria-valuenow={value} aria-valuemin={min} aria-valuemax={max}
      />
      <div className="qt-numfield__grip" role="presentation" onPointerDown={(e) => { drag.current = { x: e.clientX, v: value }; }} />
    </div>
  );
}

export const Select = ({ options, ...rest }: { options: Array<{ value: string; label: string }> } & React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className="qt-select" {...rest}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
);

export const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} className="qt-toggle" onClick={() => onChange(!checked)} />
);

export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="qt-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Roving-tabindex tabs — ←/→ move, Home/End jump; matches the panel-stack tab bar. */
export function Tabs<T extends string>({ value, tabs, onChange }: {
  value: T; tabs: Array<{ value: T; label: string; badge?: ReactNode; stale?: boolean }>; onChange: (v: T) => void;
}) {
  const i = tabs.findIndex((t) => t.value === value);
  return (
    <div className="qt-tabs" role="tablist" onKeyDown={(e) => {
      const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (d) { e.preventDefault(); onChange(tabs[(i + d + tabs.length) % tabs.length].value); }
      if (e.key === 'Home') onChange(tabs[0].value);
      if (e.key === 'End') onChange(tabs[tabs.length - 1].value);
    }}>
      {tabs.map((t) => (
        <button key={t.value} role="tab" type="button" className="qt-tab" aria-selected={value === t.value}
          tabIndex={value === t.value ? 0 : -1} data-stale={t.stale || undefined} onClick={() => onChange(t.value)}>
          {t.label}{t.badge}
        </button>
      ))}
    </div>
  );
}

export type BadgeTone = 'bull' | 'bear' | 'warn' | 'info' | 'violet' | 'neutral';
export const Badge = ({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) => (
  <span className={`qt-badge qt-badge--${tone}`}>{children}</span>
);
export const Tag = ({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) => (
  <span className="qt-tag">{children}{onRemove && <button type="button" aria-label="Remove" className="qt-iconbtn" onClick={onRemove}>×</button>}</span>
);

/* ──────────────────────────────────────────────────────── SCALE BADGE ──
   The base listing fixes the price scale an asset's chart / levels / alerts
   live in (CLAUDE.md). 1000SHIB @ 0.0048 and SHIB @ 0.0000048 are the same
   asset at a ×1000 scale gap — a badge that names both the venue and the
   multiplier is what stops "scale confusion" across the two products. */

/** `1000SHIBUSDT` → `{ base: 'SHIB', mult: 1000 }`; `BTCUSDT` → `{ base:'BTC', mult:1 }`.
 *  Handles the two shapes Binance uses: a `1000`/`1M` prefix and a `SHIB1000` suffix. */
export function parseSymbolScale(symbol: string): { base: string; mult: number; quote: string } {
  const m = /^(.*?)(USDT|USDC|USD|BUSD|PERP)?$/i.exec(symbol.toUpperCase()) ?? [];
  let core = m[1] || symbol.toUpperCase();
  const quote = m[2] || '';
  let mult = 1;
  const pre = /^(1M|1000000|100000|10000|1000|100)(.+)$/.exec(core);
  const suf = /^(.+?)(1M|1000000|100000|10000|1000|100)$/.exec(core);
  if (pre) { mult = pre[1] === '1M' ? 1_000_000 : Number(pre[1]); core = pre[2]; }
  else if (suf) { mult = suf[2] === '1M' ? 1_000_000 : Number(suf[2]); core = suf[1]; }
  return { base: core, mult, quote };
}

/** `BINANCE` or, when the base contract is a scaled one, `BINANCE · ×1000`. */
export function ScaleBadge({ symbol, venue, title }: { symbol: string; venue?: string; title?: string }) {
  const { mult } = parseSymbolScale(symbol);
  const v = (venue || 'binance').toUpperCase();
  const scaled = mult > 1;
  return (
    <span
      className="qt-badge qt-badge--neutral"
      title={title ?? `Price, chart and levels use ${v}${scaled ? ` · this contract is ${mult}× the coin` : ''}`}
      style={{ fontFamily: 'var(--font-mono)', letterSpacing: '.04em' }}
    >
      {v}{scaled && <span style={{ color: 'var(--warn)' }}> · ×{mult >= 1_000_000 ? `${mult / 1_000_000}M` : mult >= 1000 ? `${mult / 1000}k` : mult}</span>}
    </span>
  );
}

export const Spinner = ({ size = 12 }: { size?: number }) => (
  <span role="status" aria-label="Loading" style={{ width: size, height: size, display: 'inline-block', border: '1.5px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'qt-spin 700ms linear infinite' }} />
);
export const Skeleton = ({ w = '100%', h = 10 }: { w?: number | string; h?: number }) => (
  <div className="qt-skel" style={{ width: w, height: h }} aria-hidden />
);

/** Shape carries the state as much as hue (DESIGN_SYSTEM D3): ok is a calm
 *  solid dot, slow adds a pulsing ring, down a static ring. The gentle
 *  opacity pulse ("live" heartbeat) is the site's `.live-dot`, kept as the
 *  canon for both products. */
export const ConnectionDot = ({ state, label }: { state: 'ok' | 'slow' | 'down'; label?: string }) => (
  <span className="qt-conn" data-state={state} title={label ?? state} aria-label={`Connection ${state}`} role="img" />
);

/* ─────────────────────────────────────────────────────────── PRICE ── */
/** Flash on change without layout shift: fixed width from tabular-nums, animation on
 *  background+colour only. Key on the animation so repeated ticks retrigger it. */
export function PriceCell({ value, precision = 2, format, className }: { value: number; precision?: number; format?: (v: number) => string; className?: string }) {
  const prev = useRef(value);
  const [dir, setDir] = useState<'up' | 'dn' | null>(null);
  const [tick, setTick] = useState(0);
  useLayoutEffect(() => {
    if (value !== prev.current && Number.isFinite(value) && Number.isFinite(prev.current)) {
      setDir(value > prev.current ? 'up' : 'dn'); setTick((t) => t + 1);
    }
    prev.current = value;
  }, [value]);
  return <span key={tick} className={`qt-price num${className ? ` ${className}` : ''}`} data-dir={dir ?? undefined}>{format ? format(value) : value.toFixed(precision)}</span>;
}

/* ─────────────────────────────────────────────────────────── TABLE ── */
export interface Column<R> { key: string; label: string; width?: number; align?: 'left' | 'right'; render?: (row: R) => ReactNode; }
/** Windowed dense table: sticky head, tabular numbers, single-click sort, row virtualisation
 *  above `virtualizeFrom` rows (screener runs 580 assets — audit §1.1). */
export function Table<R extends { id: string }>({ rows, columns, sort, onSort, selectedId, onSelect, rowHeight = 22, virtualizeFrom = 60, height = 320 }: {
  rows: R[]; columns: Array<Column<R>>; sort?: { key: string; dir: 'asc' | 'desc' };
  onSort?: (key: string) => void; selectedId?: string; onSelect?: (id: string) => void;
  rowHeight?: number; virtualizeFrom?: number; height?: number;
}) {
  const [scroll, setScroll] = useState(0);
  const virtual = rows.length > virtualizeFrom;
  const start = virtual ? Math.max(0, Math.floor(scroll / rowHeight) - 6) : 0;
  const end = virtual ? Math.min(rows.length, start + Math.ceil(height / rowHeight) + 12) : rows.length;
  const slice = rows.slice(start, end);
  return (
    <div style={{ height, overflow: 'auto' }} onScroll={(e) => virtual && setScroll(e.currentTarget.scrollTop)}>
      <table className="qt-table" style={{ height: virtual ? rows.length * rowHeight : undefined }}>
        <thead><tr>{columns.map((c) => (
          <th key={c.key} style={{ width: c.width, textAlign: c.align }} onClick={() => onSort?.(c.key)}
            aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>{c.label}</th>
        ))}</tr></thead>
        <tbody>
          {virtual && start > 0 && <tr style={{ height: start * rowHeight }} aria-hidden><td colSpan={columns.length} /></tr>}
          {slice.map((r) => (
            <tr key={r.id} aria-selected={selectedId === r.id} onClick={() => onSelect?.(r.id)} style={{ height: rowHeight }}>
              {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }}>{c.render ? c.render(r) : (r as never)[c.key]}</td>)}
            </tr>
          ))}
          {virtual && end < rows.length && <tr style={{ height: (rows.length - end) * rowHeight }} aria-hidden><td colSpan={columns.length} /></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────────────────────────────────── PANEL FRAME ── */
export function PanelFrame({ title, symbol, pinned, onPin, connection = 'ok', focused, actions, children, onMenu }: {
  title: string; symbol?: string; pinned?: boolean; onPin?: () => void;
  connection?: 'ok' | 'slow' | 'down'; focused?: boolean; actions?: ReactNode;
  children: ReactNode; onMenu?: (e: React.MouseEvent) => void;
}) {
  return (
    <section className="qt-panel" data-focused={focused || undefined} aria-label={title}>
      <header className="qt-panel__hd" onContextMenu={onMenu}>
        <ConnectionDot state={connection} />
        <span className="qt-panel__title">{title}</span>
        {symbol && (
          <button type="button" className="qt-iconbtn" style={{ width: 'auto', padding: '0 4px' }} onClick={onPin}
            aria-pressed={pinned} title={pinned ? `Pinned to ${symbol}` : 'Follows global symbol'}>
            <span className="num" style={{ fontSize: 'var(--fs-xs)', color: pinned ? 'var(--acc)' : 'var(--low)' }}>{pinned ? '⦿ ' : '○ '}{symbol}</span>
          </button>
        )}
        <span style={{ flex: 1 }} />
        {actions}
        <IconButton label="Panel menu" onClick={onMenu as never}>⋯</IconButton>
      </header>
      <div className="qt-panel__body">{children}</div>
    </section>
  );
}

/* ────────────────────────────────────────────── FLOATING SURFACES ── */
// Every one of these is portaled to document.body (owner 2026-09-12:
// "окно статистики... открывается внутри окон с сделками" — a Modal
// opened from inside another Modal's children rendered as a plain nested
// DOM node, and .qt-modal's own `backdrop-filter` establishes a new
// containing block for `position: fixed` descendants (same rule as
// `transform`/`filter`/`will-change`) — so the INNER modal's fixed scrim
// was being positioned relative to the OUTER modal's box instead of the
// viewport, reading as "trapped inside" it. A portal sidesteps the whole
// containing-block question by moving the DOM node out of the tree
// entirely, same pattern the site already uses for its own overlays
// (ContextMenu.jsx, HelpTip.jsx, QuantScreener.jsx) — root-caused once
// here so no other floating surface in this file, or built on it, can hit
// the same trap.
export function Modal({ title, onClose, width = 560, children, footer, accent }: {
  title: string; onClose: () => void; width?: number; children: ReactNode; footer?: ReactNode;
  /** A 3px left stripe in this colour — the site's situation/detail look. */
  accent?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Initial focus runs once on mount only. Re-running it on every render
  // (onClose gets a fresh identity each time) stole focus back to the first
  // button — the close ×, before the input — on every keystroke in a prompt.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]')?.focus();
    return () => { prev?.focus(); };
  }, []);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && ref.current) { // focus trap
        const f = ref.current.querySelectorAll<HTMLElement>('button, input, select, [href], [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('keydown', key); };
  }, [onClose]);
  return createPortal(
    <div className="qt-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="qt-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ width, borderLeft: accent ? `3px solid ${accent}` : undefined }}>
        <header style={{ display: 'flex', alignItems: 'center', height: 32, padding: '0 8px 0 12px', borderBottom: '1px solid var(--line2)' }}>
          <h2 id={titleId} style={{ margin: 0, font: '600 var(--fs-md)/1 var(--font-ui)', color: 'var(--hi)' }}>{title}</h2>
          <span style={{ flex: 1 }} /><IconButton label="Close" onClick={onClose}>×</IconButton>
        </header>
        <div style={{ padding: 12, maxHeight: '70vh', overflow: 'auto' }}>{children}</div>
        {footer && <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, padding: 8, borderTop: '1px solid var(--line2)' }}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export type Toast = { id: string; tone: BadgeTone; title: string; detail?: string; ttl?: number };
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});
export const useToast = () => useContext(ToastCtx);
export function NotificationLane({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setItems((s) => [...s, { ...t, id }]);
    setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), t.ttl ?? 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div role="log" aria-live="polite" style={{ position: 'fixed', right: 8, bottom: 30, display: 'flex', flexDirection: 'column', gap: 6, zIndex: 'var(--z-toast)' as never }}>
          {items.map((t) => (
            <div key={t.id} className="qt-toast" style={{ borderLeftColor: `var(--${t.tone === 'info' ? 'acc' : t.tone})`, width: 260 }}>
              <div><div style={{ color: 'var(--hi)', fontWeight: 600 }}>{t.title}</div>{t.detail && <div style={{ color: 'var(--mid)', marginTop: 2 }}>{t.detail}</div>}</div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

/** CSS-only tooltip: no JS timers, no hover handlers, nothing to leak. */
export const Tooltip = ({ text, children }: { text: string; children: ReactNode }) => (
  <span style={{ position: 'relative', display: 'inline-flex' }} data-tip={text} title={text}>{children}</span>
);

export function ContextMenu({ x, y, items, onClose }: {
  x: number; y: number; onClose: () => void;
  items: Array<{ label: string; hint?: string; danger?: boolean; onSelect: () => void } | 'sep'>;
}) {
  useEffect(() => {
    const off = () => onClose();
    window.addEventListener('pointerdown', off); window.addEventListener('blur', off);
    return () => { window.removeEventListener('pointerdown', off); window.removeEventListener('blur', off); };
  }, [onClose]);
  return createPortal(
    <div className="qt-menu" role="menu" style={{ position: 'fixed', left: x, top: y, minWidth: 180 }}>
      {items.map((it, i) => it === 'sep'
        ? <div key={i} style={{ height: 1, background: 'var(--line2)', margin: '3px 0' }} />
        : <button key={i} role="menuitem" data-danger={it.danger || undefined} onClick={() => { it.onSelect(); onClose(); }}>
            <span>{it.label}</span>{it.hint && <kbd style={{ color: 'var(--low)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{it.hint}</kbd>}
          </button>)}
    </div>,
    document.body,
  );
}

export function CommandPalette({ open, commands, onClose }: {
  open: boolean; onClose: () => void;
  commands: Array<{ id: string; label: string; group: string; hint?: string; run: () => void }>;
}) {
  const [q, setQ] = useState(''); const [i, setI] = useState(0);
  const hits = useMemo(() => commands.filter((c) => c.label.toLowerCase().includes(q.toLowerCase())), [commands, q]);
  useEffect(() => { setI(0); }, [q]);
  if (!open) return null;
  return createPortal(
    <div className="qt-scrim" style={{ alignItems: 'flex-start', paddingTop: '14vh' }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="qt-modal" role="dialog" aria-modal="true" aria-label="Command palette" style={{ width: 520 }}>
        <input autoFocus className="qt-input" placeholder="Type a command or symbol…" value={q}
          style={{ height: 34, border: 0, borderBottom: '1px solid var(--line2)', borderRadius: 0, background: 'transparent', fontSize: 'var(--fs-md)' }}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setI((v) => Math.min(hits.length - 1, v + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setI((v) => Math.max(0, v - 1)); }
            if (e.key === 'Enter') { hits[i]?.run(); onClose(); }
            if (e.key === 'Escape') onClose();
          }} />
        <div role="listbox" style={{ maxHeight: 300, overflow: 'auto', padding: 4 }}>
          {hits.map((c, n) => (
            <div key={c.id} role="option" aria-selected={n === i} onMouseDown={() => { c.run(); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, height: 26, padding: '0 8px', borderRadius: 2, background: n === i ? 'var(--acc-deep)' : undefined, cursor: 'pointer' }}>
              <span className="lbl" style={{ width: 68 }}>{c.group}</span>
              <span style={{ color: 'var(--hi)' }}>{c.label}</span><span style={{ flex: 1 }} />
              {c.hint && <kbd className="num" style={{ color: 'var(--low)', fontSize: 'var(--fs-xs)' }}>{c.hint}</kbd>}
            </div>
          ))}
          {!hits.length && <div style={{ padding: 12, color: 'var(--low)' }}>No command matches “{q}”.</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The one coin search. Replaces the five implementations collapsed into A's .coin-search. */
export function SearchField({ value, onChange, placeholder = 'Search symbol…' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, height: 'var(--ctrl-md)', padding: '0 6px', background: 'var(--s1)', border: '1px solid var(--line2)', borderRadius: 'var(--r-input)' }}>
      <span aria-hidden style={{ color: 'var(--low)' }}>⌕</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} role="searchbox"
        style={{ flex: 1, minWidth: 0, background: 'none', border: 0, color: 'var(--hi)', font: 'var(--fs-sm)/1 var(--font-ui)', outline: 'none' }} />
      {value && <button type="button" aria-label="Clear" className="qt-iconbtn" onClick={() => onChange('')}>×</button>}
    </div>
  );
}

export const EmptyState = ({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }) => (
  <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 16, textAlign: 'center', gap: 6 }}>
    <div style={{ color: 'var(--mid)', fontWeight: 600 }}>{title}</div>
    {detail && <div style={{ color: 'var(--low)', maxWidth: 280 }}>{detail}</div>}
    {action}
  </div>
);

/* ─────────────────────────────────────────────────────── FEED STATE ──
   One honesty contract for a live surface, shared by both products
   (DESIGN_SYSTEM §9). Never fake zeros: a panel with no feed says so.
   - ok           → children as-is
   - reconnecting → children dimmed + frozen (caller keeps the last frame),
                    amber footer
   - stale/down   → children dimmed harder + struck, pink footer with the
                    age of the data
   `age` is a preformatted string ("2m 10s", "14:03"); the caller owns the
   clock. */
export function FeedState({ state, age, note, children }: {
  state: 'ok' | 'reconnecting' | 'stale'; age?: string; note?: string; children: ReactNode;
}) {
  if (state === 'ok') return <>{children}</>;
  const down = state === 'stale';
  return (
    <div className="qt-feed" data-state={state}>
      <div className="qt-feed__body">{children}</div>
      <div className="qt-feed__foot" role="status">
        <ConnectionDot state={down ? 'down' : 'slow'} />
        <span>{note ?? (down ? 'Feed stale' : 'Reconnecting…')}{age ? ` · ${age}` : ''}</span>
      </div>
    </div>
  );
}
